import {
  type CollisionDetection,
  closestCenter,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  type SortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { SortableItemBase } from '../types';

/**
 * useSortableListの引数
 * @template T - SortableItemBaseを拡張するアイテム型
 */
type Args<T extends SortableItemBase> = {
  /** ソート可能なアイテムの配列 */
  items: T[];
  /** アイテムの順序が変更されたときのコールバック */
  onReorder: (items: T[]) => void;
};

/**
 * useSortableListの型定義
 * @template T - SortableItemBaseを拡張するアイテム型
 */
type IUseSortableList = <T extends SortableItemBase>({
  items,
  onReorder,
}: Args<T>) => {
  /** ドラッグ&ドロップの状態 */
  state: {
    /** センサーの設定（ポインター・キーボード） */
    sensors: ReturnType<typeof useSensors>;
    /** 衝突検出アルゴリズム */
    collisionDetection: CollisionDetection;
    /** ソート戦略 */
    sortingStrategy: SortingStrategy;
  };
  /** ドラッグ&ドロップのアクション */
  actions: {
    /** ドラッグ終了時のハンドラー */
    handleDragEnd: (event: DragEndEvent) => void;
  };
};

/**
 * SortableListのロジックを管理するカスタムフック
 *
 * @template T - SortableItemBaseを拡張するアイテム型
 * @param {Args<T>} args - フックの引数
 * @param {T[]} args.items - ソート可能なアイテムの配列
 * @param {function} args.onReorder - アイテムの順序が変更されたときのコールバック
 * @returns ドラッグ&ドロップの状態とアクション
 *
 * @example
 * const { state, actions } = useSortableList({ items, onReorder });
 */
export const useSortableList: IUseSortableList = ({ items, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      onReorder(reorderedItems);
    }
  };

  return {
    state: {
      sensors,
      collisionDetection: closestCenter,
      sortingStrategy: verticalListSortingStrategy,
    },
    actions: {
      handleDragEnd,
    },
  };
};
